import * as React from "react";
import { ReactNode, useContext } from "react";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap, SxProps } from "@mui/material";
import IconButton, { IconButtonOwnProps } from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { TimelineGridContext } from "./timelinePlannerGrid";
import MuiMenu, { IMuiMenuItemProps } from "../framework/muiMenu";

interface TimelineGridCellProps {
    startColumn: string;
    endColumn: string;
    children: ReactNode;
}

export const TimelineGridCell = (props: TimelineGridCellProps) => {
    const context = useContext(TimelineGridContext);

    // Don't render if doesn't overlap with date range.
    if (!context.isOverlapping(props.startColumn, props.endColumn)) return null;

    const gridColumnStart = context.getColumnForDate(props.startColumn);
    let gridColumnEnd = gridColumnStart;
    if (props.endColumn) {
        gridColumnEnd =
            context.getColumnForDate(props.endColumn) || gridColumnStart;
    } else {
        // This should not happen, but some data might not have this.
        console.warn("TimelineGridCell endColumn prop missing");
    }

    return (
        <div
            style={{ gridColumn: `${gridColumnStart}/${gridColumnEnd + 1}` }}
            className="timeline-grid-cell"
        >
            {props.children}
        </div>
    );
};

interface TimelineGridCellActionsRowProps {
    children: ReactNode;
    actionButtons?: TimelineGridCellActionButtonProps[];
}

export const TimelineGridCellActionsRow = ({
    children,
    actionButtons,
}: TimelineGridCellActionsRowProps) => (
    <Stack direction="row" justifyContent="space-between">
        {children}
        {actionButtons?.length > 0 ? (
            <Stack direction="row" justifyContent="end">
                {actionButtons.map((b, i) => (
                    <TimelineGridCellActionButton
                        key={b.title}
                        sx={i > 0 ? { ml: 0.5 } : undefined}
                        {...b}
                    />
                ))}
            </Stack>
        ) : null}
    </Stack>
);

interface TimelineGridCellActionButtonProps {
    title: string;
    color: IconButtonOwnProps["color"];
    Icon: OverridableComponent<SvgIconTypeMap<any, "svg">>;
    onClick?: () => void;
    sx?: SxProps;
    menuItems?: IMuiMenuItemProps[];
}

const TimelineGridCellActionButton = ({
    title,
    color,
    Icon,
    onClick,
    sx,
    menuItems,
}: TimelineGridCellActionButtonProps) => {
    const buttonSx = {
        p: 0,
        "&:hover": { color: `${color}.dark` },
        ...sx,
    };

    return menuItems?.length > 0 ? (
        <MuiMenu
            label={<Icon color={color} fontSize="inherit" />}
            size="small"
            items={menuItems}
            noBox
            buttonSx={buttonSx}
        />
    ) : (
        <IconButton
            title={title}
            size="small"
            color={color}
            sx={buttonSx}
            onClick={onClick}
        >
            <Icon fontSize="inherit" />
        </IconButton>
    );
};
